/**
 * InviteCodeModal - 邀请码管理弹窗
 * 管理员可以生成和管理邀请码
 */

import React, { useState, useEffect } from 'react';
import { X, Ticket, Copy, Check, Trash2, Loader2, RefreshCw, CheckCircle } from 'lucide-react';
import api from '../api';
import AlertToast from './AlertToast';
import { copyTextToClipboard } from '../utils/browserCompat'; // http 站点没有 navigator.clipboard

export default function InviteCodeModal({ open, onClose }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState(null); // 记录刚复制的邀请码ID
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [durationDays, setDurationDays] = useState('');
  const [membershipLevel, setMembershipLevel] = useState('education');  // 邀请码版本

  // Toast 自动消失
  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ message: '', type: 'success' }), 2500);
      return () => clearTimeout(timer);
    }
  }, [toast.message]);

  // 加载邀请码列表
  const loadCodes = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/invite-codes');
      setCodes(data);
    } catch (err) {
      console.error('加载邀请码失败:', err);
      setToast({ message: '加载邀请码失败', type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadCodes();
    }
  }, [open]);

  // 生成邀请码
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body = {};
      if (durationDays && parseInt(durationDays, 10) > 0) body.duration_days = parseInt(durationDays, 10);
      body.membership_level = membershipLevel;
      const result = await api.post('/api/admin/invite-codes/generate', body);
      if (result.success) {
        setToast({ message: '成功生成 5 个邀请码', type: 'success' });
        loadCodes();
      }
    } catch (err) {
      console.error('生成邀请码失败:', err);
      setToast({ message: err.message || '生成失败', type: 'warning' });
    } finally {
      setGenerating(false);
    }
  };

  // 复制邀请码
  const handleCopy = async (code, id) => {
    if (await copyTextToClipboard(code)) {
      setCopiedId(id);
      setToast({ message: '已复制到剪贴板', type: 'success' });
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      setToast({ message: '复制失败', type: 'warning' });
    }
  };

  // 删除邀请码
  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/invite-codes/${id}`);
      setCodes(prev => prev.filter(c => c.id !== id));
      setToast({ message: '邀请码已删除', type: 'success' });
    } catch (err) {
      setToast({ message: err.message || '删除失败', type: 'warning' });
    }
  };

  // 格式化日期
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!open) return null;

  // 统计
  const unusedCount = codes.filter(c => !c.used).length;
  const usedCount = codes.filter(c => c.used).length;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.8)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        width: 520, maxHeight: '80vh', background: 'var(--bg-panel)',
        borderRadius: 16, overflow: 'hidden',
        border: '1px solid var(--border-subtle)',
        display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Ticket size={20} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
                邀请码管理
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                可用 {unusedCount} 个 · 已使用 {usedCount} 个
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              padding: '8px', cursor: 'pointer', color: 'var(--text-muted)'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Actions */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--modal-input-border)',
          display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0
        }}>
          <select
            value={membershipLevel}
            onChange={e => {
              setMembershipLevel(e.target.value);
              // 教育版默认永久（清空天数）；商用/VIP 需手动填天数
              if (e.target.value === 'education') setDurationDays('');
            }}
            style={{
              padding: '10px 8px', borderRadius: 8, flexShrink: 0,
              background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer'
            }}
            title="邀请码对应的会员版本"
          >
            <option value="education">教育版</option>
            <option value="commercial">商用版</option>
            <option value="vip">VIP版</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <input
              type="number" min="0" placeholder="天数"
              value={durationDays}
              onChange={e => setDurationDays(e.target.value)}
              style={{
                width: 60, padding: '10px 8px', borderRadius: 8, textAlign: 'center',
                background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)', fontSize: 13, outline: 'none'
              }}
              title="留空=永久，填写数字=有效天数"
            />
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>天</span>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 8,
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              border: 'none', color: '#fff', fontSize: 14, fontWeight: 500,
              cursor: generating ? 'not-allowed' : 'pointer',
              opacity: generating ? 0.6 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
            }}
          >
            {generating ? (
              <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> 生成中...</>
            ) : (
              <><Ticket size={16} /> 生成 5 个邀请码</>
            )}
          </button>
          <button
            onClick={loadCodes}
            disabled={loading}
            style={{
              padding: '12px 16px', borderRadius: 8,
              background: 'var(--modal-input-bg)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-secondary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            title="刷新列表"
          >
            <RefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>

        {/* Code List */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '8px 24px 24px'
        }}>
          {loading ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 40, color: 'var(--text-muted)'
            }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : codes.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: 40, color: 'var(--text-muted)'
            }}>
              <Ticket size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>暂无邀请码</p>
              <p style={{ fontSize: 13 }}>点击上方按钮生成邀请码</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {codes.map(code => (
                <div
                  key={code.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', borderRadius: 10,
                    background: code.used ? 'var(--modal-input-bg)' : 'rgba(139,92,246,0.08)',
                    border: `1px solid ${code.used ? 'var(--modal-input-border)' : 'rgba(139,92,246,0.2)'}`,
                    opacity: code.used ? 0.6 : 1
                  }}
                >
                  {/* 邀请码 */}
                  <code style={{
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: 15, fontWeight: 600,
                    color: code.used ? 'var(--text-muted)' : '#a78bfa',
                    letterSpacing: '2px',
                    textDecoration: code.used ? 'line-through' : 'none'
                  }}>
                    {code.code}
                  </code>

                  {/* 状态 */}
                  <div style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>
                    {code.used ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle size={12} color="#4ade80" />
                        {code.used_by_username || '已使用'} · {formatDate(code.used_at)}
                      </span>
                    ) : (
                      <span>
                        {{ education: '教育版', commercial: '商用版', vip: 'VIP版' }[code.membership_level] || '教育版'}
                        {' · '}
                        {code.duration_days ? `${code.duration_days}天` : '永久'}
                        {' · '}
                        {formatDate(code.created_at)}
                      </span>
                    )}
                  </div>

                  {/* 操作按钮 */}
                  {!code.used && (
                    <button
                      onClick={() => handleCopy(code.code, code.id)}
                      style={{
                        background: 'var(--modal-input-bg)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 6, padding: '6px 10px',
                        color: copiedId === code.id ? '#4ade80' : 'var(--text-secondary)',
                        cursor: 'pointer', fontSize: 12,
                        display: 'flex', alignItems: 'center', gap: 4
                      }}
                    >
                      {copiedId === code.id ? <Check size={14} /> : <Copy size={14} />}
                      {copiedId === code.id ? '已复制' : '复制'}
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(code.id)}
                    style={{
                      background: 'transparent',
                      border: 'none', borderRadius: 6, padding: 6,
                      color: 'var(--text-muted)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center'
                    }}
                    title="删除"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      <AlertToast message={toast.message} type={toast.type} />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
